import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { ACCOUNTS } from '../../api/accounts';
import { PAYABLE_ACCOUNTS, fetchSupplierPayableLines } from '../../api/payables';
import { downloadCsv } from '../../utils/downloadCsv';

// Drill-down only: the per-supplier detail reads supplier-tagged lines on every liability
// account (so 2075 Rajabdeen Haji Float etc. are included); this is the fallback until that
// list has loaded. The main table uses fetchSupplierPayableLines (shared with Dashboard).
const PAYABLE_ACCOUNTS_FALLBACK = PAYABLE_ACCOUNTS;

const TODAY = new Date();
const daysBetween = (dateStr) => Math.floor((TODAY - new Date(dateStr)) / 86400000);
const ageBucket = (days) => days <= 30 ? '0-30' : days <= 60 ? '31-60' : days <= 90 ? '61-90' : '90+';
const bucketColor = { '0-30': '#16a34a', '31-60': '#d97706', '61-90': '#ea580c', '90+': '#dc2626' };
const bucketBg   = { '0-30': '#f0fdf4', '31-60': '#fffbeb', '61-90': '#fff7ed', '90+': '#fef2f2' };

export default function AccountsPayable() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('balance');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [detail, setDetail] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [supMapState, setSupMapState] = useState({});
  const [payableAccountIds, setPayableAccountIds] = useState(PAYABLE_ACCOUNTS_FALLBACK);

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
    supabase.from('chart_of_accounts').select('id, account_types!inner(category)').eq('is_active', true).eq('account_types.category', 'LIABILITY')
      .then(({ data }) => { if (data?.length) setPayableAccountIds(data.map(a => a.id)); });
  }, []);

  useEffect(() => {
    const load = async () => {
      // Read payables from the general ledger. Cost incurred = Σ base_credit (payable
      // credited at point of sale), Paid = Σ base_debit (payable cleared by a payment
      // voucher). Lines are grouped by their tagged supplier; untagged payables fall
      // into "Unallocated" so the total still reconciles to the balance sheet.
      const [{ data: suppliers }, { data: agents }, lines] = await Promise.all([
        supabase.from('suppliers').select('id, name, supplier_code, supplier_type, parent_supplier_id, is_active'),
        supabase.from('agents').select('id, tls_code, full_name'),
        fetchSupplierPayableLines({ branchId: branchFilter }),
      ]);

      const supMap = {};
      (suppliers || []).forEach(s => { supMap[s.id] = s; });
      const agentMap = {};
      (agents || []).forEach(a => { agentMap[a.id] = a; });

      // A sub-supplier (e.g. Arabian Lux, linked under Rajabdeen Haji) is who's actually
      // costed at ticket/invoice time, but the parent is who's actually paid — so its
      // balance belongs on the parent's row, not its own, or the two halves of the same
      // real payable split across two lines and neither number means anything on its own.
      // Walk parent_supplier_id to the top (cycle-guarded) before grouping.
      const topParentOf = (id) => {
        let cur = id, hops = 0;
        while (supMap[cur]?.parent_supplier_id && hops < 10) { cur = supMap[cur].parent_supplier_id; hops++; }
        return cur;
      };

      const map = {};
      const ensure = (id, partyType) => {
        const key = id || 'unallocated';
        if (!map[key]) {
          const s = id ? supMap[id] : null;
          const a = id ? agentMap[id] : null;
          const isAgent = partyType === 'agent' || !!a;
          map[key] = {
            supplier_id: id || null,
            name: s?.name || (a ? `${a.tls_code} — ${a.full_name}` : 'Unallocated'),
            supplier_code: s?.supplier_code || a?.tls_code || '',
            supplier_type: s?.supplier_type || (isAgent ? 'Agent Commission' : ''),
            invoiced: 0, paid: 0, oldestUnpaidDate: null,
          };
        }
        return map[key];
      };

      (lines || []).forEach(l => {
        const resolvedId = l.party_type === 'supplier' && l.party_id ? topParentOf(l.party_id) : l.party_id;
        const row = ensure(resolvedId, l.party_type);
        const dr = parseFloat(l.base_debit || 0);
        const cr = parseFloat(l.base_credit || 0);
        row.invoiced += cr;
        row.paid += dr;
        const date = l.journal_entries?.entry_date;
        if (cr > 0 && date && (!row.oldestUnpaidDate || date < row.oldestUnpaidDate)) row.oldestUnpaidDate = date;
      });

      const result = Object.values(map).map(r => {
        const balance = r.invoiced - r.paid;
        const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
        if (balance > 0) buckets[r.oldestUnpaidDate ? ageBucket(daysBetween(r.oldestUnpaidDate)) : '90+'] = balance;
        return { ...r, balance, buckets };
      })
        .filter(r => r.invoiced > 0 || r.paid > 0)
        // Inactive (e.g. merged) suppliers with nothing left owing don't get a row.
        .filter(r => !(r.supplier_id && supMap[r.supplier_id]?.is_active === false && Math.abs(r.balance) < 0.5));
      setRows(result);
      setSupMapState(supMap);
      setLoading(false);
    };
    load();
  }, [branchFilter]);

  const sorted = useMemo(() => {
    let r = rows.filter(r => !search || r.name?.toLowerCase().includes(search.toLowerCase()) || r.supplier_code?.toLowerCase().includes(search.toLowerCase()) || r.supplier_type?.toLowerCase().includes(search.toLowerCase()));
    r = [...r].sort((a, b) => {
      const av = typeof a[sortKey] === 'string' ? a[sortKey].toLowerCase() : (a[sortKey] || 0);
      const bv = typeof b[sortKey] === 'string' ? b[sortKey].toLowerCase() : (b[sortKey] || 0);
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return r;
  }, [rows, search, sortKey, sortDir]);

  const totals = useMemo(() => sorted.reduce((acc, r) => {
    acc.invoiced += r.invoiced; acc.paid += r.paid; acc.balance += r.balance;
    // Aging only applies to amounts actually owed; suppliers we've overpaid / prepaid
    // (debit balance) are tracked separately so owed − advances = net, and the four
    // buckets always sum to "owed".
    if (r.balance > 0) acc.owed += r.balance; else acc.advances += r.balance;
    Object.keys(r.buckets).forEach(b => acc.buckets[b] += r.buckets[b]);
    return acc;
  }, { invoiced: 0, paid: 0, balance: 0, owed: 0, advances: 0, buckets: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 } }), [sorted]);

  const handleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };
  const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const handleExport = () => {
    if (!sorted.length) return;
    const rows = [
      ['Supplier Code', 'Supplier Name', 'Type', 'Cost Incurred', 'Paid', 'Balance Payable', '0-30 Days', '31-60 Days', '61-90 Days', '90+ Days'],
      ...sorted.map(r => [r.supplier_code, r.name, r.supplier_type, r.invoiced.toFixed(2), r.paid.toFixed(2), r.balance.toFixed(2), r.buckets['0-30'].toFixed(2), r.buckets['31-60'].toFixed(2), r.buckets['61-90'].toFixed(2), r.buckets['90+'].toFixed(2)]),
      ['', '', 'Totals', totals.invoiced.toFixed(2), totals.paid.toFixed(2), totals.balance.toFixed(2), totals.buckets['0-30'].toFixed(2), totals.buckets['31-60'].toFixed(2), totals.buckets['61-90'].toFixed(2), totals.buckets['90+'].toFixed(2)],
    ];
    downloadCsv(rows, `accounts-payable-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const openSupplier = async (supplier) => {
    if (selectedSupplier?.supplier_id === supplier.supplier_id) { setSelectedSupplier(null); return; }
    setSelectedSupplier(supplier);
    setDetailLoading(true);
    // Cost breakdown reads straight from the ledger, not the legacy tickets/visas/
    // booking_suppliers tables — those only get populated by the old standalone-entry
    // flows. Costs entered via invoice costing (the current, dominant path) post directly
    // to journal_lines with no matching row in those tables, so this used to show "None"
    // for suppliers who very much have real, ledger-accurate cost activity.
    // Include any sub-suppliers linked under this one (e.g. Arabian Lux under Rajabdeen
    // Haji) — their cost lines/payments are part of this same real payable, just tagged
    // to the child at entry time.
    const childIds = Object.values(supMapState).filter(s => s.parent_supplier_id === supplier.supplier_id).map(s => s.id);
    const allIds = [supplier.supplier_id, ...childIds];

    const [{ data: costLines }, { data: pv }, { data: balLines }] = await Promise.all([
      supabase.from('journal_lines')
        .select('id, base_credit, description, account_id, journal_entries!inner(id, entry_date, entry_number, reference_type, reference_id, status, is_reversed, reversal_of)')
        .eq('party_type', 'supplier').in('party_id', allIds)
        .in('account_id', payableAccountIds)
        .eq('journal_entries.status', 'POSTED').eq('journal_entries.is_reversed', false).is('journal_entries.reversal_of', null)
        .gt('base_credit', 0),
      supabase.from('payment_vouchers').select('id, voucher_number, voucher_date, amount, currency:currency_id(code), payment_mode').in('supplier_id', allIds).order('voucher_date', { ascending: false }),
      // Full net balance per entity (not just cost lines) — this is what actually shows
      // how much of the combined payable is Rajabdeen Haji's own vs. each linked
      // sub-supplier's, not just where the cost line items came from.
      supabase.from('journal_lines')
        .select('party_id, base_debit, base_credit, journal_entries!inner(status, is_reversed, reversal_of)')
        .eq('party_type', 'supplier').in('party_id', allIds).in('account_id', payableAccountIds)
        .eq('journal_entries.status', 'POSTED').eq('journal_entries.is_reversed', false).is('journal_entries.reversal_of', null),
    ]);

    const balMap = {};
    (balLines || []).forEach(l => {
      if (!balMap[l.party_id]) balMap[l.party_id] = { credit: 0, debit: 0 };
      balMap[l.party_id].credit += parseFloat(l.base_credit || 0);
      balMap[l.party_id].debit += parseFloat(l.base_debit || 0);
    });
    const breakdown = allIds.map(id => ({
      id, name: supMapState[id]?.name || 'Unknown',
      balance: (balMap[id]?.credit || 0) - (balMap[id]?.debit || 0),
    })).filter(r => r.balance !== 0).sort((a, b) => b.balance - a.balance);

    const BUCKET_ACCOUNT = { [ACCOUNTS.AIRLINE_PAYABLE]: 'tickets', [ACCOUNTS.VISA_PAYABLE]: 'visas', [ACCOUNTS.HOTEL_LAND_PAYABLE]: 'packages' };
    const buckets = { tickets: [], visas: [], packages: [], other: [] };
    (costLines || []).forEach(l => {
      const je = l.journal_entries;
      const refType = je?.reference_type;
      const refId = je?.reference_id;
      const drillPath = (refType && refType !== 'PAYMENT_VOUCHER' && refId) ? `/invoices/${refId}` : (je?.id ? `/journals/${je.id}` : null);
      const row = { id: l.id, entry_date: je?.entry_date, description: l.description, amount: parseFloat(l.base_credit || 0), drill_path: drillPath };
      (buckets[BUCKET_ACCOUNT[l.account_id]] || buckets.other).push(row);
    });
    Object.values(buckets).forEach(b => b.sort((a, b2) => (b2.entry_date || '').localeCompare(a.entry_date || '')));

    setDetail({ ...buckets, payments: pv || [], breakdown });
    setDetailLoading(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Accounts Payable</div>
          <div className="page-subtitle">Outstanding supplier balances with aging</div>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={handleExport}>Download CSV</button>
          <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <div className="stat-card" style={{ borderLeft: '3px solid #dc2626' }}>
          <div className="stat-label">Owed to Suppliers</div>
          <div className="stat-value" style={{ color: '#dc2626' }}>LKR {totals.owed.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Suppliers in credit — aged below</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid #16a34a' }}>
          <div className="stat-label">Less: Supplier Advances</div>
          <div className="stat-value" style={{ color: '#16a34a' }}>LKR {totals.advances.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Suppliers overpaid / prepaid (debit balance)</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid #1f2937' }}>
          <div className="stat-label">Net Payable</div>
          <div className="stat-value">LKR {totals.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Matches Dashboard</div>
        </div>
      </div>

      {/* Aging buckets */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        {['0-30', '31-60', '61-90', '90+'].map(b => (
          <div key={b} style={{ background: bucketBg[b], border: `1px solid ${bucketColor[b]}30`, borderRadius: 8, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: bucketColor[b], marginBottom: 4 }}>{b} DAYS</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: bucketColor[b] }}>LKR {totals.buckets[b].toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          </div>
        ))}
      </div>

      {/* Search + Branch filter */}
      <div className="card no-print" style={{ padding: '12px 16px', marginBottom: 0, borderRadius: '10px 10px 0 0', display: 'flex', gap: 12, alignItems: 'center' }}>
        <input className="form-input" style={{ maxWidth: 320 }} placeholder="Search supplier name, code or type…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-input" style={{ maxWidth: 200 }} value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
          <option value="">All Branches</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
              {[['name', 'Supplier'], ['balance', 'Balance Payable']].map(([key, label]) => (
                <th key={key} onClick={() => handleSort(key)} style={{ padding: '10px 14px', textAlign: key === 'name' ? 'left' : 'right', cursor: 'pointer', userSelect: 'none', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                  {label}{arrow(key)}
                </th>
              ))}
              {['0-30', '31-60', '61-90', '90+'].map(b => (
                <th key={b} style={{ padding: '10px 10px', textAlign: 'right', fontSize: 11, color: bucketColor[b], fontWeight: 700 }}>{b}d</th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => (
              <React.Fragment key={row.supplier_id}>
                <tr
                  onClick={() => openSupplier(row)}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selectedSupplier?.supplier_id === row.supplier_id ? 'rgba(79,110,247,0.04)' : '' }}
                  onMouseEnter={e => { if (selectedSupplier?.supplier_id !== row.supplier_id) e.currentTarget.style.background = 'var(--bg)'; }}
                  onMouseLeave={e => { if (selectedSupplier?.supplier_id !== row.supplier_id) e.currentTarget.style.background = ''; }}
                >
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontWeight: 600 }}>{row.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.supplier_type}{row.supplier_code ? ` · ${row.supplier_code}` : ''}</div>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: row.balance > 0 ? '#dc2626' : '#16a34a' }}>{row.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  {['0-30', '31-60', '61-90', '90+'].map(b => (
                    <td key={b} style={{ padding: '10px 10px', textAlign: 'right', color: row.buckets[b] > 0 ? bucketColor[b] : 'var(--text-muted)', fontWeight: row.buckets[b] > 0 ? 600 : 400, fontSize: 12 }}>
                      {row.buckets[b] > 0 ? row.buckets[b].toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
                    </td>
                  ))}
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    {row.supplier_id && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={e => { e.stopPropagation(); navigate(`/ledger/suppliers?supplier=${row.supplier_id}`); }}
                      >
                        Ledger
                      </button>
                    )}
                  </td>
                </tr>

                {/* Drill-down */}
                {selectedSupplier?.supplier_id === row.supplier_id && (
                  <tr><td colSpan={7} style={{ padding: 0, background: '#f8fafc' }}>
                    {detailLoading ? (
                      <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" /></div>
                    ) : (
                      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        {detail.breakdown?.length > 1 && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Balance by Sub-Category</div>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              {detail.breakdown.map(b => (
                                <div key={b.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', minWidth: 140 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600 }}>{b.name}</div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: b.balance > 0 ? '#dc2626' : '#16a34a' }}>{b.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                </div>
                              ))}
                              <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', minWidth: 140 }}>
                                <div style={{ fontSize: 11, fontWeight: 700 }}>Total (combined)</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: row.balance > 0 ? '#dc2626' : '#16a34a' }}>{row.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
                          {[
                            ['tickets', 'Ticket Costs'],
                            ['visas', 'Visa Costs'],
                            ['packages', 'Package Costs'],
                            ['other', 'Other Costs'],
                          ].map(([key, label]) => (
                            <div key={key}>
                              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
                              {!detail[key]?.length ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>None</div> : detail[key].map(row => (
                                <div key={row.id} onClick={() => row.drill_path && navigate(row.drill_path)} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12, cursor: row.drill_path ? 'pointer' : 'default' }}>
                                  <div><span style={{ color: 'var(--primary)', fontWeight: 600 }}>{row.entry_date?.slice(0, 10)}</span><span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{row.description}</span></div>
                                  <span>LKR {row.amount.toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                        {/* Payments */}
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Payments Made</div>
                          {detail.payments?.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>None</div> : detail.payments?.map((p, i) => (
                            <div key={i} onClick={() => navigate(`/vouchers/payment/${p.id}`)} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12, cursor: 'pointer' }}>
                              <div><span style={{ fontWeight: 600, color: 'var(--primary)' }}>{p.voucher_number}</span><span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{p.voucher_date?.slice(0, 10)} · {p.payment_mode}</span></div>
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>{p.currency?.code} {parseFloat(p.amount || 0).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--bg)', borderTop: '2px solid var(--border)', fontWeight: 700 }}>
              <td style={{ padding: '10px 14px' }}>TOTAL ({sorted.length} suppliers)</td>
              <td style={{ padding: '10px 14px', textAlign: 'right', color: totals.balance > 0 ? '#dc2626' : '#16a34a' }}>{totals.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
              {['0-30', '31-60', '61-90', '90+'].map(b => (
                <td key={b} style={{ padding: '10px 10px', textAlign: 'right', color: bucketColor[b], fontSize: 12 }}>{totals.buckets[b].toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
              ))}
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
